import React, { useState, useEffect } from 'react';
import { Avatar, makeStyles, Tooltip, useMediaQuery } from '@material-ui/core';
import { Button } from 'components/Base';
import Icons from 'components/Icons';
import Store from 'store/store';
import { useAuth } from 'providers/AuthProvider/SessionAuthProvider';
import env from 'consts/env';
import Routes from 'consts/routes';
import { useHistory } from 'react-router-dom';
import MessageCenter from 'components/MessageCenter/MessageCenter';
import SettingsRoundedIcon from '@material-ui/icons/SettingsRounded';
import HomeRoundedIcon from '@material-ui/icons/HomeRounded';
import { Prompt } from 'components/Base';

const { connect, selectors, actions } = Store;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 'calc(100%)',
    width: '100%',
    color: 'black',
    paddingLeft: '0.5rem',
    '@media (max-width: 768px)': {
      '& .MuiButton-label': {
        fontSize: '0.7rem'
      }
    }
  },
  wrapper: {
    display: 'flex',
    flex: 1
  },
  imgBox: {
    width: '69px',
    height: '69px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    svg: {
      height: '2rem'
    }
  },
  title: {
    marginRight: '1rem',
    display: 'flex',
    flexDirection: 'column',
    height: '65px',
    alignItems: 'flex-start',
    justifyContent: 'center',
    alignContent: 'center',
    lineHeight: 1.2,
    cursor: 'help',
    textOverflow: 'elipsis',
    paddingTop: '0.2rem',
    maxWidth: '300px'
  },
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'white'
  },
  flexAlignCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  logoWrapper: {
    paddingLeft: '1rem',
    marginRight: '2rem',
    '& img': {
      maxWidth: '100%',
      maxHeight: 'calc(100% - 1rem)',
      minWidth: '100px'
    },
    '@media (max-width: 768px)': {
      paddingLeft: '0.5rem',
      marginRight: '1rem'
    }
  },
  avatar: {
    color: '#000',
    backgroundColor: '#FFF',
    fontSize: '0.75rem',
    height: '30px',
    width: '30px',
    lineHeight: '30px',
    cursor: 'pointer',
    fontWeight: 600,
    border: '1px solid #000'
  }
});

interface Props {
  firmLogo?: any;
}

const Header = ({ firmLogo }: Props) => {
  const { logout, user } = useAuth();
  const classes = useStyles();
  const history = useHistory();
  const [initials, setInitials] = useState('');
  const [isActive, setIsActive] = useState(true);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const advisor = user as any;
  const [isModalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    if (!advisor.full_name) {
      return;
    }

    setIsActive(!advisor.organization?.trial_expired);

    setInitials(
      advisor.full_name
        .split(' ')
        .map((name: string) => name[0])
        .join('')
    );
  }, [advisor]);

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div
          className={`${classes.logoWrapper} ${classes.flexAlignCenter}`}
          style={{ cursor: 'pointer' }}
          onClick={() => history.push(Routes.HOMEPAGE)}
        >
          {firmLogo ? (
            <img
              src={firmLogo}
              alt={`${
                advisor.organization
                  ? advisor.organization.name
                  : 'organization'
              } logo`}
            />
          ) : isMobile ? (
            <Icons.Logo />
          ) : (
            <Icons.LogoWithText />
          )}
        </div>
        <div
          className={`${classes.wrapper}`}
          style={{ justifyContent: 'flex-end' }}
        >
          {isActive && (
            <>
              <div className={classes.flexAlignCenter}>
                <Button
                  onClick={() =>
                    history.push({
                      pathname: Routes.CASES,
                      state: { newCase: true }
                    })
                  }
                  type="button"
                >
                  Add a new case
                </Button>
                <Button
                  onClick={() => history.push(Routes.CASES)}
                  type="button"
                >
                  View all cases
                </Button>
              </div>
              <div
                className={classes.imgBox}
                style={{ marginRight: '-1.5rem' }}
              >
                <HomeRoundedIcon
                  onClick={() => history.push(Routes.HOMEPAGE)}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <div
                className={classes.imgBox}
                style={{ marginRight: '-1.5rem' }}
              >
                <SettingsRoundedIcon
                  onClick={() => history.push(Routes.SETTINGS)}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <MessageCenter />
            </>
          )}
          <div className={classes.imgBox} style={{ marginLeft: '-1.5rem' }}>
            <Avatar
              className={classes.avatar}
              onClick={() => history.push(Routes.USER_SETTINGS)}
            >
              {initials}
            </Avatar>
          </div>
          <Tooltip title="Logout" arrow>
            <div className={classes.imgBox} style={{ marginLeft: '-1.5rem' }}>
              <Icons.LogoutIcon
                onClick={() => setModalVisible(true)}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </Tooltip>
          <Prompt
            isOpen={isModalVisible}
            title={`Are you sure you wish to log out?`}
            confirmAction={() => logout({ returnTo: env.REDIRECT_LOGOUT_URL })}
            cancelAction={() => {
              setModalVisible(false);
            }}
            isProcessing={false}
          />
        </div>
      </div>
    </div>
  );
};

export default connect(
  (state: any) => ({
    firmLogo: selectors.firmSelectors.documentUrl(state)
  }),
  {
    clearCase: actions.caseActions.clearCase
  }
)(Header);
